import { useTranslate } from "@similarweb/i18n-provider";
import { Info24px } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, Combobox, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { IWebsitesFavicons } from "app/services/sitesService/types";

import { FilterOption } from "../FilterOption";
import type { CompetitorOption, ValueMap } from "../types";

import { useIsSecondaryDomainsAllowed } from "./useIsSecondaryDomainsAllowed";

interface ISecondaryCompetitorsSectionProps {
    options: CompetitorOption[];
    tempSelectedItems: ValueMap;
    favicons?: IWebsitesFavicons;
    onItemSelect: (item: CompetitorOption) => void;
}

export const SecondaryCompetitorsSection = ({
    options,
    tempSelectedItems,
    favicons,
    onItemSelect,
}: ISecondaryCompetitorsSectionProps) => {
    const { translate } = useTranslate();
    const isAllowed = useIsSecondaryDomainsAllowed();

    if (!options.length) {
        return null;
    }

    const disabledTooltipContent = translate(
        "activation.alerts.competitors.dropdown.secondary.disabled-tooltip",
    );

    return (
        <Combobox.Section key="secondary-competitors">
            <Combobox.SectionHeader>
                <Flex
                    alignItems="center"
                    gap="4px"
                >
                    {translate("activation.alerts.competitors.dropdown.secondary.section-header")}
                    <Tooltip
                        content={translate("activation.alerts.competitors.dropdown.secondary.info-tooltip")}
                    >
                        <Info24px
                            size="sm"
                            color={colorsPalettes.carbon[300]}
                            data-automation="competitors-filter-secondary-info-icon"
                        />
                    </Tooltip>
                </Flex>
            </Combobox.SectionHeader>
            {options.map((item) => (
                <FilterOption
                    key={item.id}
                    item={item}
                    onClick={isAllowed ? () => onItemSelect(item) : () => undefined}
                    favicon={favicons?.[item.id]}
                    isDisabled={!isAllowed}
                    disabledTooltipContent={disabledTooltipContent}
                    chip={
                        <Badge data-automation="competitors-filter-secondary-badge">
                            {translate("activation.alerts.competitors.dropdown.secondary.badge")}
                        </Badge>
                    }
                >
                    <Combobox.OptionCheckbox isChecked={tempSelectedItems.has(item.id)} />
                </FilterOption>
            ))}
        </Combobox.Section>
    );
};
